import * as THREE from "three";
import { ConvexHull } from "three/examples/jsm/math/ConvexHull.js";
import { GLOW_ATTRIBUTE, seeded } from "./facet";

// Pre-cut fragments for breakable props (phase C, gate C5; decision 0042: pots and crates shatter into rigid pieces).
// A convex shape (its points: a crate's corners, a pot's rings) is cut by seeded planes, the biggest piece first,
// until it is in `pieces` convex fragments. Each fragment keeps its hull points about its own centre (a physics
// engine's convex-hull collider takes them as they are) and its faces, each marked as on the original surface or a
// fresh cut, so the view paints the outside and the inside apart. Pure and seeded: the same points and seed give the
// same fragments; a game cuts each breakable once at load, by its placement's seed.

/** [x, y, z]. */
export type P3 = [number, number, number];

/** One fragment. */
export interface Fragment {
  /** Its centre (the mean of its hull's corners) in the shape's frame. */
  centre: P3;
  /** Its hull's corners, about `centre`. */
  points: P3[];
  /** Its hull's triangles (indices into `points`), wound outward, and whether each lies on the original surface. */
  tris: [number, number, number][];
  surface: boolean[];
  /** Its volume (m³). */
  volume: number;
}

/** How to cut. */
export interface FractureOptions {
  seed: number;
  /** How many fragments (≥ 1). */
  pieces: number;
  /** A cut is through a point this far (a fraction of the piece's size) off its centre, at most. Default 0.25. */
  offset?: number;
  /** A cut that leaves a side smaller than this share of the piece's volume is tried again. Default 0.15. */
  minShare?: number;
}

interface Hull {
  points: P3[];
  tris: [number, number, number][];
  planes: { n: P3; c: number }[];
  volume: number;
  centre: P3;
}

const EPS = 1e-6;

function hullOf(points: readonly P3[]): Hull | null {
  if (points.length < 4) return null;
  let h: ConvexHull;
  try {
    h = new ConvexHull().setFromPoints(points.map((p) => new THREE.Vector3(p[0], p[1], p[2])));
  } catch {
    return null;
  }
  if (!h.faces.length) return null;
  const out: P3[] = [];
  const index = new Map<THREE.Vector3, number>();
  const at = (v: THREE.Vector3): number => {
    let i = index.get(v);
    if (i === undefined) {
      i = out.length;
      out.push([v.x, v.y, v.z]);
      index.set(v, i);
    }
    return i;
  };
  const tris: [number, number, number][] = [];
  const planes: { n: P3; c: number }[] = [];
  for (const f of h.faces) {
    const loop: number[] = [];
    let e = f.edge;
    do {
      loop.push(at(e.head().point));
      e = e.next;
    } while (e !== f.edge);
    for (let i = 1; i + 1 < loop.length; i++) {
      tris.push([loop[0]!, loop[i]!, loop[i + 1]!]);
      planes.push({ n: [f.normal.x, f.normal.y, f.normal.z], c: f.constant });
    }
  }
  const centre: P3 = [0, 0, 0];
  for (const p of out) for (let k = 0; k < 3; k++) centre[k]! += p[k]! / out.length;
  let volume = 0;
  for (const [a, b, c] of tris) {
    const A = out[a]!;
    const B = out[b]!;
    const C = out[c]!;
    const ax = A[0] - centre[0], ay = A[1] - centre[1], az = A[2] - centre[2];
    const bx = B[0] - centre[0], by = B[1] - centre[1], bz = B[2] - centre[2];
    const cx = C[0] - centre[0], cy = C[1] - centre[1], cz = C[2] - centre[2];
    volume += (ax * (by * cz - bz * cy) - ay * (bx * cz - bz * cx) + az * (bx * cy - by * cx)) / 6;
  }
  if (!(volume > EPS)) return null;
  return { points: out, tris, planes, volume, centre };
}

/** Cut a convex shape (given by points whose hull it is) into `pieces` convex fragments, seeded. */
export function fracture(points: readonly P3[], o: FractureOptions): Fragment[] {
  const rng = seeded(o.seed);
  const whole = hullOf(points);
  if (!whole) throw new Error("fracture: the points make no solid");
  const offset = o.offset ?? 0.25;
  const minShare = o.minShare ?? 0.15;
  let pieces: Hull[] = [whole];
  while (pieces.length < Math.max(1, o.pieces)) {
    // the biggest piece (the first of equals) is cut next
    let bi = 0;
    for (let i = 1; i < pieces.length; i++) if (pieces[i]!.volume > pieces[bi]!.volume + EPS) bi = i;
    const p = pieces[bi]!;
    let size = 0;
    for (const q of p.points) size = Math.max(size, Math.hypot(q[0] - p.centre[0], q[1] - p.centre[1], q[2] - p.centre[2]));
    let cut: [Hull, Hull] | null = null;
    for (let tries = 0; tries < 12 && !cut; tries++) {
      // a random direction (uniform on the sphere), a plane through a point near the centre
      const z = rng() * 2 - 1;
      const a = rng() * Math.PI * 2;
      const r = Math.sqrt(1 - z * z);
      const n: P3 = [r * Math.cos(a), z, r * Math.sin(a)];
      const shift = (rng() * 2 - 1) * offset * size;
      const d = n[0] * p.centre[0] + n[1] * p.centre[1] + n[2] * p.centre[2] + shift;
      const side = p.points.map((q) => n[0] * q[0] + n[1] * q[1] + n[2] * q[2] - d);
      const pos: P3[] = [];
      const neg: P3[] = [];
      p.points.forEach((q, i) => (side[i]! > 0 ? pos : neg).push(q));
      if (pos.length === 0 || neg.length === 0) continue;
      // where the plane crosses the segments between the two sides: the cut face's corners (and some inside it,
      // which the hulls drop)
      p.points.forEach((q, i) => {
        if (side[i]! <= 0) return;
        p.points.forEach((w, j) => {
          if (side[j]! > 0) return;
          const t = side[i]! / (side[i]! - side[j]!);
          const x: P3 = [q[0] + (w[0] - q[0]) * t, q[1] + (w[1] - q[1]) * t, q[2] + (w[2] - q[2]) * t];
          pos.push(x);
          neg.push(x);
        });
      });
      const A = hullOf(pos);
      const B = hullOf(neg);
      if (!A || !B || A.volume < minShare * p.volume || B.volume < minShare * p.volume) continue;
      cut = [A, B];
    }
    if (!cut) break; // nothing cuts well any more: fewer pieces than asked
    pieces = [...pieces.slice(0, bi), cut[0], cut[1], ...pieces.slice(bi + 1)];
  }
  return pieces.map((h) => ({
    centre: [...h.centre] as P3,
    points: h.points.map((q) => [q[0] - h.centre[0], q[1] - h.centre[1], q[2] - h.centre[2]] as P3),
    tris: h.tris.map((t) => [...t] as [number, number, number]),
    // a face on one of the whole shape's planes is outside; any other is a fresh cut
    surface: h.planes.map((pl) => whole.planes.some((w) => w.n[0] * pl.n[0] + w.n[1] * pl.n[1] + w.n[2] * pl.n[2] > 0.999 && Math.abs(w.c - pl.c) < 1e-4)),
    volume: h.volume,
  }));
}

/** A fragment as a flat-shaded, vertex-coloured geometry about its centre: outside faces `outer`, cut faces `inner`,
 *  each face tinted a little by the seed (the low-poly look's facets), glow 0. */
export function fragmentGeometry(f: Fragment, outer: THREE.Color, inner: THREE.Color, o: { tint?: number; seed?: number } = {}): THREE.BufferGeometry {
  const rng = seeded(o.seed ?? 1);
  const tint = o.tint ?? 0.06;
  const pos = new Float32Array(f.tris.length * 9);
  const col = new Float32Array(f.tris.length * 9);
  f.tris.forEach(([a, b, c], i) => {
    const k = 1 + (rng() * 2 - 1) * tint;
    const base = f.surface[i] ? outer : inner;
    [a, b, c].forEach((v, j) => {
      const p = f.points[v]!;
      pos.set(p, i * 9 + j * 3);
      col.set([base.r * k, base.g * k, base.b * k], i * 9 + j * 3);
    });
  });
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.BufferAttribute(pos, 3));
  g.setAttribute("color", new THREE.BufferAttribute(col, 3));
  g.setAttribute(GLOW_ATTRIBUTE, new THREE.BufferAttribute(new Float32Array(f.tris.length * 3), 1));
  g.computeVertexNormals();
  return g;
}

/** A box's corners (half extents), for `fracture`. */
export const boxPoints = (hx: number, hy: number, hz: number): P3[] => {
  const out: P3[] = [];
  for (const x of [-hx, hx]) for (const y of [-hy, hy]) for (const z of [-hz, hz]) out.push([x, y, z]);
  return out;
};

/** A body of revolution's rings (radius at each height, `sides` around), for `fracture` (its convex hull is cut). */
export const ringPoints = (profile: readonly [number, number][], sides: number): P3[] => {
  const out: P3[] = [];
  for (const [r, y] of profile)
    for (let i = 0; i < sides; i++) {
      const a = (i / sides) * Math.PI * 2;
      out.push(r < 1e-9 ? [0, y, 0] : [Math.cos(a) * r, y, Math.sin(a) * r]);
      if (r < 1e-9) break;
    }
  return out;
};
